import React, { useState, useEffect, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import SEO from '../components/SEO';
import { destinationAPI } from '../services/api';
import { toUploadUrl } from '../config/api';

const DestinationDetail = () => {
    const { id } = useParams();
    const [destination, setDestination] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [activeImage, setActiveImage] = useState(0);
    const [showAllActivities, setShowAllActivities] = useState(false);

    // Helper function to get image URL
    const getImageUrl = (imagePath) => {
        return toUploadUrl(imagePath, {
            fallback: 'https://placehold.co/1200x600/1a1a1a/ffffff?text=No+Image'
        });
    };

    const fetchDestination = useCallback(async () => {
        try {
            setLoading(true);
            setError(null);
            const response = await destinationAPI.getDestinationById(id);
            setDestination(response.data);
        } catch (err) {
            setError(err.response?.data?.message || 'Failed to load destination details');
            console.error('Error fetching destination:', err);
        } finally {
            setLoading(false);
        }
    }, [id]);

    useEffect(() => {
        fetchDestination();
    }, [fetchDestination]);

    // Scroll to top on page load
    useEffect(() => {
        window.scrollTo(0, 0);
    }, []);

    if (loading) {
        return (
            <div className="loading-container">
                <div className="spinner"></div>
                <p>Loading destination details...</p>
            </div>
        );
    }

    if (error || !destination) {
        return (
            <div className="error-container">
                <i className="fas fa-exclamation-circle"></i>
                <h3>Destination not found</h3>
                <p>{error || 'The destination you are looking for does not exist.'}</p>
                <Link to="/destinations" className="back-btn">
                    Browse All Destinations
                </Link>
            </div>
        );
    }

    const displayedActivities = showAllActivities 
        ? destination.activities 
        : destination.activities?.slice(0, 6);

    return (
        <div className="destination-detail-page">
            <SEO
                title={`${destination.name} | Destination Guide | RedGoTrip`}
                description={`${destination.name}${destination.region ? `, ${destination.region}` : ''} travel guide with best time to visit, weather, top activities and holiday planning tips on RedGoTrip.`}
                canonicalPath={`/destinations/${destination.id}`}
                keywords={`${destination.name}, ${destination.region || ''}, destination guide, RedGoTrip`}
                image={destination?.image ? getImageUrl(destination.image) : undefined}
                jsonLd={{
                    '@context': 'https://schema.org',
                    '@type': 'TouristDestination',
                    name: destination.name,
                    description: destination.title || `${destination.name} destination guide`,
                    image: destination?.image ? getImageUrl(destination.image) : undefined
                }}
            />
            {/* Hero Section with Parallax Effect */}
            <section 
                className="detail-hero" 
                style={{ 
                    backgroundImage: `url(${getImageUrl(destination.image)})` 
                }}
            >
                <div className="hero-overlay"></div>
                <div className="hero-content">
                    <span className="hero-badge">
                        <i className="fas fa-map-marker-alt"></i> Destination
                    </span>
                    <h1 className="hero-title">{destination.name}</h1>
                    <p className="hero-subtitle">{destination.title || 'Beautiful Paradise in Kashmir'}</p>
                </div>
                
            </section>

            <div className="container">
                {/* Quick Stats Bar */}
                <div className="quick-stats">
                    <div className="stat-item">
                        <i className="fas fa-mountain"></i>
                        <div className="stat-info">
                            <span className="stat-label">Altitude</span>
                            <span className="stat-value">{destination.altitude || 'N/A'}</span>
                        </div>
                    </div>
                    <div className="stat-item">
                        <i className="fas fa-temperature-high"></i>
                        <div className="stat-info">
                            <span className="stat-label">Summer</span>
                            <span className="stat-value">{destination.weather?.summer || '15-30°C'}</span>
                        </div>
                    </div>
                    <div className="stat-item">
                        <i className="fas fa-snowflake"></i>
                        <div className="stat-info">
                            <span className="stat-label">Winter</span>
                            <span className="stat-value">{destination.weather?.winter || '-2-10°C'}</span>
                        </div>
                    </div>
                    <div className="stat-item">
                        <i className="fas fa-umbrella"></i>
                        <div className="stat-info">
                            <span className="stat-label">Best Time</span>
                            <span className="stat-value">{destination.best_time || 'All Year'}</span>
                        </div>
                    </div>
                </div>

                {/* Main Content Grid */}
                <div className="content-grid">
                    {/* Left Column - Main Content */}
                    <div className="main-content">
                        {/* About Section */}
                        <div className="about-section card">
                            <h2><i className="fas fa-info-circle"></i> About {destination.name}</h2>
                            <p className="description">
                                {destination.long_description || destination.description || 'No description available.'}
                            </p>
                        </div>

                        {/* Highlights Section */}
                        {destination.highlights && destination.highlights.length > 0 && (
                            <div className="highlights-section card">
                                <h2><i className="fas fa-star"></i> Highlights</h2>
                                <div className="highlights-grid">
                                    {destination.highlights.map((item, index) => (
                                        <div key={index} className="highlight-item">
                                            <i className="fas fa-check-circle"></i>
                                            <span>{item}</span>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}

                        {/* Activities Section */}
                        {destination.activities && destination.activities.length > 0 && (
                            <div className="activities-section card">
                                <div className="section-header">
                                    <h2><i className="fas fa-hiking"></i> Things to Do</h2>
                                    {destination.activities.length > 6 && (
                                        <button 
                                            className="toggle-btn"
                                            onClick={() => setShowAllActivities(!showAllActivities)}
                                        >
                                            {showAllActivities ? 'Show Less' : `View All (${destination.activities.length})`}
                                        </button>
                                    )}
                                </div>
                                <div className="activities-grid">
                                    {displayedActivities.map((activity, index) => (
                                        <div key={index} className="activity-item">
                                            <i className="fas fa-check"></i>
                                            <span>{activity}</span>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}

                        {/* Gallery Section */}
                        {destination.gallery && destination.gallery.length > 0 && (
                            <div className="gallery-wrapper">
                                <div className="gallery-header">
                                    <h2><i className="fas fa-camera"></i> Gallery</h2>
                                    <p className="gallery-subtitle">Explore the beauty of {destination.name} through these stunning photos</p>
                                </div>

                                {/* Main Featured Image */}
                                <div className="featured-image-container">
                                    <div className="featured-image-wrapper">
                                        <img 
                                            src={getImageUrl(destination.gallery[activeImage])} 
                                            alt={`${destination.name} - View ${activeImage + 1}`}
                                            className="featured-image"
                                            onError={(e) => {
                                                e.target.onerror = null;
                                                e.target.src = 'https://placehold.co/1200x600/1a1a1a/ffffff?text=No+Image';
                                            }}
                                        />
                                        <div className="image-counter-badge">
                                            <i className="fas fa-images"></i>
                                            <span>{activeImage + 1} / {destination.gallery.length}</span>
                                        </div>
                                        <button 
                                            className="nav-arrow prev-arrow"
                                            onClick={() => setActiveImage(prev => 
                                                prev === 0 ? destination.gallery.length - 1 : prev - 1
                                            )}
                                        >
                                            <i className="fas fa-chevron-left"></i>
                                        </button>
                                        <button 
                                            className="nav-arrow next-arrow"
                                            onClick={() => setActiveImage(prev => 
                                                prev === destination.gallery.length - 1 ? 0 : prev + 1
                                            )}
                                        >
                                            <i className="fas fa-chevron-right"></i>
                                        </button>
                                    </div>
                                </div>

                                {/* Thumbnail Strip */}
                                <div className="thumbnail-strip">
                                    {destination.gallery.map((img, index) => (
                                        <button
                                            key={index}
                                            className={`thumbnail-item ${activeImage === index ? 'active-thumbnail' : ''}`}
                                            onClick={() => setActiveImage(index)}
                                        >
                                            <div className="thumbnail-image-wrapper">
                                                <img 
                                                    src={getImageUrl(img)} 
                                                    alt={`Thumbnail ${index + 1}`}
                                                    onError={(e) => {
                                                        e.target.onerror = null;
                                                        e.target.src = 'https://placehold.co/150x100/1a1a1a/ffffff?text=Error';
                                                    }}
                                                />
                                                {activeImage === index && (
                                                    <div className="thumbnail-active-indicator">
                                                        <i className="fas fa-check"></i>
                                                    </div>
                                                )}
                                            </div>
                                        </button>
                                    ))}
                                </div>
                            </div>
                        )}
                    </div>

                    {/* Right Column - Booking Sidebar */}
                    <div className="sidebar">
                        <div className="booking-card">
                            <div className="card-header">
                                <h3>Plan Your Trip</h3>
                                <p>Ready to explore {destination.name}?</p>
                            </div>

                            <div className="price-info">
                                <span className="price-label">Starting from</span>
                                <span className="price">₹12,999</span>
                                <span className="price-note">per person</span>
                            </div>

                            <Link to={`/packages?destination=${destination.region || destination.name.toLowerCase()}`} className="packages-btn">
                                <span>View Packages</span>
                                <i className="fas fa-arrow-right"></i>
                            </Link>

                            <div className="quick-facts">
                                <h4>Quick Facts</h4>
                                <ul>
                                    <li>
                                        <i className="fas fa-language"></i>
                                        <span>Language: Kashmiri, Hindi, English</span>
                                    </li>
                                    <li>
                                        <i className="fas fa-currency"></i>
                                        <span>Currency: INR</span>
                                    </li>
                                    <li>
                                        <i className="fas fa-plug"></i>
                                        <span>Plug Type: C, D, M</span>
                                    </li>
                                    <li>
                                        <i className="fas fa-phone"></i>
                                        <span>Country Code: +91</span>
                                    </li>
                                </ul>
                            </div>

                            <div className="contact-support">
                                <h4>Need Assistance?</h4>
                                <p>Our travel experts are here to help</p>
                                <a href="tel:+919335444047" className="support-link">
                                    <i className="fas fa-phone-alt"></i>
                                    +91 9335444047
                                </a>
                                <a href="tel:+917895300727" className="support-link">
                                    <i className="fas fa-phone-alt"></i>
                                    +91 7895300727
                                </a>
                                <a href="mailto:info@redgotrips.com" className="support-link email">
                                    <i className="fas fa-envelope"></i>
                                    info@redgotrips.com
                                </a>
                            </div>

                            <div className="features">
                                <div className="feature-item">
                                    <i className="fas fa-wifi"></i>
                                    <span>Free WiFi</span>
                                </div>
                                <div className="feature-item">
                                    <i className="fas fa-hotel"></i>
                                    <span>Premium Stays</span>
                                </div>
                                <div className="feature-item">
                                    <i className="fas fa-utensils"></i>
                                    <span>Local Cuisine</span>
                                </div>
                                <div className="feature-item">
                                    <i className="fas fa-car"></i>
                                    <span>Transport</span>
                                </div>
                                <div className="feature-item">
                                    <i className="fas fa-map"></i>
                                    <span>Local Guide</span>
                                </div>
                                <div className="feature-item">
                                    <i className="fas fa-shield-alt"></i>
                                    <span>Safe Travel</span>
                                </div>
                            </div>
                        </div>

                        {/* Related Destinations */}
                        <div className="related-card">
                            <h4>Popular Destinations</h4>
                            <div className="related-list">
                                <Link to="/destinations/1" className="related-item">
                                    <i className="fas fa-map-marker-alt"></i>
                                    <span>Srinagar</span>
                                </Link>
                                <Link to="/destinations/2" className="related-item">
                                    <i className="fas fa-map-marker-alt"></i>
                                    <span>Gulmarg</span>
                                </Link>
                                <Link to="/destinations/3" className="related-item">
                                    <i className="fas fa-map-marker-alt"></i>
                                    <span>Pahalgam</span>
                                </Link>
                                <Link to="/destinations/4" className="related-item">
                                    <i className="fas fa-map-marker-alt"></i>
                                    <span>Sonmarg</span>
                                </Link>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Custom CSS */}
            <style>{`
                .destination-detail-page {
                    background: linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%);
                    min-height: 100vh;
                }

                .container {
                    max-width: 1400px;
                    margin: 0 auto;
                    padding: 0 2rem;
                }

                /* Hero Section */
                .detail-hero {
                    height: 70vh;
                    min-height: 600px;
                    background-size: cover;
                    background-position: center;
                    background-attachment: fixed;
                    position: relative;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    color: white;
                    text-align: center;
                }

                .hero-overlay {
                    position: absolute;
                    top: 0;
                    left: 0;
                    right: 0;
                    bottom: 0;
                    background: linear-gradient(135deg, rgba(0,0,0,0.4) 0%, rgba(0,0,0,0.8) 100%);
                }

                .hero-content {
                    position: relative;
                    z-index: 2;
                    max-width: 900px;
                    padding: 0 2rem;
                    animation: fadeInUp 1s ease;
                }

                .hero-badge {
                    display: inline-flex;
                    align-items: center;
                    gap: 0.5rem;
                    background: rgba(255,255,255,0.2);
                    backdrop-filter: blur(10px);
                    padding: 0.5rem 1.5rem;
                    border-radius: 50px;
                    font-size: 14px;
                    font-weight: 600;
                    letter-spacing: 1px;
                    margin-bottom: 2rem;
                    border: 1px solid rgba(255,255,255,0.3);
                }

                .hero-badge i {
                    color: #ffd700;
                }

                .hero-title {
                    font-size: 72px;
                    font-weight: 800;
                    margin-bottom: 1rem;
                    text-shadow: 2px 2px 4px rgba(0,0,0,0.3);
                    line-height: 1.1;
                }

                .hero-subtitle {
                    font-size: 20px;
                    margin-bottom: 2rem;
                    opacity: 0.95;
                    font-weight: 300;
                    letter-spacing: 1px;
                }

                .hero-meta {
                    display: flex;
                    gap: 2rem;
                    justify-content: center;
                    flex-wrap: wrap;
                }

                .hero-meta .meta-item {
                    display: flex;
                    align-items: center;
                    gap: 1rem;
                    background: rgba(255,255,255,0.15);
                    backdrop-filter: blur(10px);
                    padding: 1rem 2rem;
                    border-radius: 60px;
                    border: 1px solid rgba(255,255,255,0.2);
                    transition: all 0.3s;
                }

                .hero-meta .meta-item:hover {
                    background: rgba(255,255,255,0.25);
                    transform: translateY(-2px);
                }

                .hero-meta .meta-item i {
                    font-size: 24px;
                    color: #ffd700;
                }

                .hero-meta .meta-item strong {
                    display: block;
                    font-size: 18px;
                    font-weight: 700;
                    line-height: 1.2;
                    color:#fff;
                }

                .hero-meta .meta-item span {
                    font-size: 14px;
                    opacity: 0.9;
                    color:#fff;
                }

                .hero-wave {
                    position: absolute;
                    bottom: 0;
                    left: 0;
                    right: 0;
                }

                .hero-wave svg {
                    width: 100%;
                    height: auto;
                    display: block;
                    fill: #f8fafc;
                }

                /* Quick Stats */
                .quick-stats {
                    display: grid;
                    grid-template-columns: repeat(4, 1fr);
                    gap: 1.5rem;
                    margin-top: -3rem;
                    margin-bottom: 3rem;
                    position: relative;
                    z-index: 10;
                }

                .stat-item {
                    background: white;
                    border-radius: 20px;
                    padding: 1.5rem;
                    display: flex;
                    align-items: center;
                    gap: 1rem;
                    box-shadow: 0 10px 30px rgba(0,0,0,0.1);
                    transition: all 0.3s;
                    animation: fadeInUp 0.5s ease;
                }

                .stat-item:hover {
                    transform: translateY(-5px);
                    box-shadow: 0 20px 40px rgba(102,126,234,0.15);
                }

                .stat-item i {
                    font-size: 36px;
                    color: #334f7f;
                    background: linear-gradient(135deg, #334f7f20 0%, #764ba220 100%);
                    width: 60px;
                    height: 60px;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    border-radius: 15px;
                }

                .stat-info {
                    display: flex;
                    flex-direction: column;
                }

                .stat-label {
                    font-size: 13px;
                    color: #718096;
                    text-transform: uppercase;
                    letter-spacing: 0.5px;
                }

                .stat-value {
                    font-size: 18px;
                    font-weight: 700;
                    color: #2d3748;
                }

                /* Content Grid */
                .content-grid {
                    display: grid;
                    grid-template-columns: 2fr 1fr;
                    gap: 2rem;
                    margin-bottom: 2rem;
                }

                /* Cards */
                .card {
                    background: white;
                    border-radius: 24px;
                    padding: 2rem;
                    margin-bottom: 2rem;
                    box-shadow: 0 10px 30px rgba(0,0,0,0.05);
                    transition: all 0.3s;
                }

                .card:hover {
                    box-shadow: 0 20px 40px rgba(102,126,234,0.1);
                }

                .card h2 {
                    font-size: 24px;
                    color: #2d3748;
                    margin-bottom: 1.5rem;
                    display: flex;
                    align-items: center;
                    gap: 0.75rem;
                }

                .card h2 i {
                    color: #334f7f;
                    font-size: 28px;
                }

                /* About Section */
                .description {
                    color: #4a5568;
                    line-height: 1.9;
                    font-size: 16px;
                }

                /* Highlights Grid */
                .highlights-grid {
                    display: grid;
                    grid-template-columns: repeat(2, 1fr);
                    gap: 1rem;
                }

                .highlight-item {
                    display: flex;
                    align-items: center;
                    gap: 1rem;
                    padding: 1rem;
                    background: #f7fafc;
                    border-radius: 12px;
                    transition: all 0.3s;
                }

                .highlight-item:hover {
                    background: linear-gradient(135deg, #334f7f10 0%, #764ba210 100%);
                    transform: translateX(5px);
                }

                .highlight-item i {
                    color: #48bb78;
                    font-size: 20px;
                }

                .highlight-item span {
                    color: #4a5568;
                    font-weight: 500;
                }

                /* Activities Section */
                .section-header {
                    display: flex;
                    justify-content: space-between;
                    align-items: center;
                    margin-bottom: 1.5rem;
                }

                .toggle-btn {
                    background: none;
                    border: none;
                    color: #334f7f;
                    font-weight: 600;
                    cursor: pointer;
                    padding: 0.5rem 1rem;
                    border-radius: 8px;
                    transition: all 0.3s;
                }

                .toggle-btn:hover {
                    background: #f7fafc;
                    color: #764ba2;
                }

                .activities-grid {
                    display: grid;
                    grid-template-columns: repeat(2, 1fr);
                    gap: 1rem;
                }

                .activity-item {
                    display: flex;
                    align-items: center;
                    gap: 0.75rem;
                    padding: 0.75rem 1rem;
                    background: #f0f9ff;
                    border-radius: 10px;
                    transition: all 0.3s;
                }

                .activity-item:hover {
                    background: #e0f2fe;
                    transform: translateX(5px);
                }

                .activity-item i {
                    color: #0369a1;
                    font-size: 14px;
                }

                .activity-item span {
                    color: #0369a1;
                    font-size: 14px;
                    font-weight: 500;
                }

                /* Gallery Section */
                .gallery-wrapper {
                    margin: 2rem 0;
                    padding: 2rem;
                    background: white;
                    border-radius: 24px;
                    box-shadow: 0 10px 30px rgba(0,0,0,0.05);
                    width: 100%;
                }

                .gallery-header {
                    text-align: center;
                    margin-bottom: 2rem;
                }

                .gallery-header h2 {
                    font-size: 32px;
                    color: #2d3748;
                    margin-bottom: 0.5rem;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    gap: 0.75rem;
                }

                .gallery-header h2 i {
                    color: #334f7f;
                    font-size: 32px;
                }

                .gallery-subtitle {
                    color: #718096;
                    font-size: 16px;
                }

                /* Featured Image Container */
                .featured-image-container {
                    width: 100%;
                    margin-bottom: 1.5rem;
                    border-radius: 16px;
                    overflow: hidden;
                    background: #f8fafc;
                }

                .featured-image-wrapper {
                    position: relative;
                    width: 100%;
                    height: 500px;
                    overflow: hidden;
                }

                .featured-image {
                    width: 100%;
                    height: 100%;
                    object-fit: cover;
                    transition: transform 0.5s ease;
                }

                .featured-image-wrapper:hover .featured-image {
                    transform: scale(1.02);
                }

                /* Image Counter Badge */
                .image-counter-badge {
                    position: absolute;
                    bottom: 20px;
                    right: 20px;
                    background: rgba(0, 0, 0, 0.7);
                    backdrop-filter: blur(5px);
                    color: white;
                    padding: 0.75rem 1.25rem;
                    border-radius: 50px;
                    font-size: 14px;
                    font-weight: 600;
                    display: flex;
                    align-items: center;
                    gap: 0.5rem;
                    border: 1px solid rgba(255, 255, 255, 0.2);
                    z-index: 5;
                }

                .image-counter-badge i {
                    color: #ffd700;
                }

                /* Navigation Arrows */
                .nav-arrow {
                    position: absolute;
                    top: 50%;
                    transform: translateY(-50%);
                    width: 45px;
                    height: 45px;
                    background: rgba(255, 255, 255, 0.9);
                    border: none;
                    border-radius: 50%;
                    cursor: pointer;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    color: #2d3748;
                    font-size: 18px;
                    transition: all 0.3s;
                    z-index: 10;
                    box-shadow: 0 5px 15px rgba(0, 0, 0, 0.2);
                    border: 1px solid rgba(255, 255, 255, 0.3);
                }

                .nav-arrow:hover {
                    background: white;
                    transform: translateY(-50%) scale(1.1);
                    box-shadow: 0 10px 25px rgba(0, 0, 0, 0.3);
                    color: #334f7f;
                }

                .prev-arrow {
                    left: 20px;
                }

                .next-arrow {
                    right: 20px;
                }

                /* Thumbnail Strip */
                .thumbnail-strip {
                    display: grid;
                    grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
                    gap: 1rem;
                    margin-top: 1rem;
                }

                .thumbnail-item {
                    border: none;
                    background: none;
                    padding: 0;
                    cursor: pointer;
                    border-radius: 12px;
                    overflow: hidden;
                    transition: all 0.3s;
                }

                .thumbnail-item:hover {
                    transform: translateY(-3px);
                    box-shadow: 0 10px 20px rgba(102, 126, 234, 0.2);
                }

                .thumbnail-item.active-thumbnail {
                    transform: scale(1.02);
                    box-shadow: 0 0 0 3px #334f7f, 0 10px 20px rgba(102, 126, 234, 0.3);
                }

                .thumbnail-image-wrapper {
                    position: relative;
                    height: 100px;
                    overflow: hidden;
                    border-radius: 10px;
                }

                .thumbnail-image-wrapper img {
                    width: 100%;
                    height: 100%;
                    object-fit: cover;
                    transition: transform 0.3s;
                }

                .thumbnail-item:hover .thumbnail-image-wrapper img {
                    transform: scale(1.1);
                }

                .thumbnail-active-indicator {
                    position: absolute;
                    top: 8px;
                    right: 8px;
                    width: 24px;
                    height: 24px;
                    background: #334f7f;
                    border-radius: 50%;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    color: white;
                    font-size: 12px;
                    border: 2px solid white;
                    box-shadow: 0 2px 5px rgba(0,0,0,0.2);
                }

                /* Sidebar */
                .sidebar {
                    position: sticky;
                    top: 100px;
                    align-self: start;
                }

                .booking-card {
                    background: white;
                    border-radius: 24px;
                    padding: 2rem;
                    box-shadow: 0 20px 40px rgba(0,0,0,0.1);
                    margin-bottom: 2rem;
                }

                .card-header {
                    text-align: center;
                    margin-bottom: 2rem;
                }

                .card-header h3 {
                    font-size: 24px;
                    color: #2d3748;
                    margin-bottom: 0.5rem;
                }

                .card-header p {
                    color: #718096;
                }

                .price-info {
                    text-align: center;
                    margin-bottom: 2rem;
                    padding: 1.5rem;
                    background: linear-gradient(135deg, #334f7f10 0%, #764ba210 100%);
                    border-radius: 16px;
                }

                .price-label {
                    display: block;
                    font-size: 14px;
                    color: #718096;
                    margin-bottom: 0.5rem;
                }

                .price {
                    display: block;
                    font-size: 48px;
                    font-weight: 800;
                    color: #334f7f;
                    line-height: 1;
                    margin-bottom: 0.25rem;
                }

                .price-note {
                    font-size: 14px;
                    color: #718096;
                }

                .packages-btn {
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    gap: 0.5rem;
                    background: linear-gradient(135deg, #334f7f 0%, #764ba2 100%);
                    color: white;
                    text-decoration: none;
                    padding: 1rem;
                    border-radius: 12px;
                    font-weight: 600;
                    margin-bottom: 2rem;
                    transition: all 0.3s;
                }

                .packages-btn:hover {
                    transform: translateY(-2px);
                    box-shadow: 0 10px 25px rgba(102,126,234,0.4);
                }

                .packages-btn i {
                    transition: transform 0.3s;
                }

                .packages-btn:hover i {
                    transform: translateX(5px);
                }

                .quick-facts {
                    margin-bottom: 2rem;
                    padding: 1.5rem;
                    background: #f7fafc;
                    border-radius: 16px;
                }

                .quick-facts h4 {
                    font-size: 16px;
                    color: #2d3748;
                    margin-bottom: 1rem;
                }

                .quick-facts ul {
                    list-style: none;
                    padding: 0;
                }

                .quick-facts li {
                    display: flex;
                    align-items: center;
                    gap: 1rem;
                    padding: 0.5rem 0;
                    color: #4a5568;
                    font-size: 14px;
                }

                .quick-facts i {
                    color: #334f7f;
                    width: 20px;
                }

                .contact-support {
                    margin-bottom: 2rem;
                    padding: 1.5rem;
                    background: #f7fafc;
                    border-radius: 16px;
                    text-align: center;
                }

                .contact-support h4 {
                    font-size: 16px;
                    color: #2d3748;
                    margin-bottom: 0.5rem;
                }

                .contact-support p {
                    color: #718096;
                    font-size: 14px;
                    margin-bottom: 1rem;
                }

                .support-link {
                    display: block;
                    color: #334f7f;
                    text-decoration: none;
                    font-weight: 600;
                    margin: 0.75rem 0;
                    transition: color 0.3s;
                }

                .support-link:hover {
                    color: #764ba2;
                }

                .support-link i {
                    margin-right: 0.5rem;
                }

                .support-link.email {
                    color: #718096;
                    font-size: 14px;
                }

                .features {
                    display: grid;
                    grid-template-columns: repeat(3, 1fr);
                    gap: 1rem;
                }

                .feature-item {
                    display: flex;
                    flex-direction: column;
                    align-items: center;
                    gap: 0.5rem;
                    padding: 1rem 0.5rem;
                    background: #f7fafc;
                    border-radius: 12px;
                    transition: all 0.3s;
                }

                .feature-item:hover {
                    transform: translateY(-2px);
                    background: #edf2f7;
                }

                .feature-item i {
                    font-size: 20px;
                    color: #334f7f;
                }

                .feature-item span {
                    font-size: 11px;
                    color: #4a5568;
                    font-weight: 500;
                    text-align: center;
                }

                /* Related Card */
                .related-card {
                    background: white;
                    border-radius: 24px;
                    padding: 2rem;
                    box-shadow: 0 10px 30px rgba(0,0,0,0.05);
                }

                .related-card h4 {
                    font-size: 18px;
                    color: #2d3748;
                    margin-bottom: 1.5rem;
                }

                .related-list {
                    display: flex;
                    flex-direction: column;
                    gap: 0.75rem;
                }

                .related-item {
                    display: flex;
                    align-items: center;
                    gap: 1rem;
                    padding: 0.75rem 1rem;
                    background: #f7fafc;
                    border-radius: 10px;
                    color: #4a5568;
                    text-decoration: none;
                    transition: all 0.3s;
                }

                .related-item:hover {
                    background: #edf2f7;
                    transform: translateX(5px);
                    color: #334f7f;
                }

                .related-item i {
                    color: #334f7f;
                }

                /* Animations */
                @keyframes fadeInUp {
                    from {
                        opacity: 0;
                        transform: translateY(30px);
                    }
                    to {
                        opacity: 1;
                        transform: translateY(0);
                    }
                }

                /* Responsive */
                @media (max-width: 1200px) {
                    .hero-title {
                        font-size: 56px;
                    }
                }

                @media (max-width: 1024px) {
                    .content-grid {
                        grid-template-columns: 1fr;
                    }
                    
                    .sidebar {
                        position: static;
                    }
                    
                    .quick-stats {
                        grid-template-columns: repeat(2, 1fr);
                    }
                }

                @media (max-width: 768px) {
                    .container {
                        padding: 0 1.5rem;
                    }

                    .hero-title {
                        font-size: 42px;
                    }
                    
                    .hero-subtitle {
                        font-size: 18px;
                    }
                    
                    .hero-meta {
                        flex-direction: column;
                        gap: 1rem;
                    }
                    
                    .quick-stats {
                        grid-template-columns: repeat(2, 1fr);
                        margin-top: -2rem;
                    }
                    
                    .highlights-grid,
                    .activities-grid {
                        grid-template-columns: 1fr;
                    }
                    
                    .featured-image-wrapper {
                        height: 350px;
                    }
                    
                    .thumbnail-strip {
                        grid-template-columns: repeat(3, 1fr);
                    }
                    
                    .features {
                        grid-template-columns: repeat(2, 1fr);
                    }
                }

                @media (max-width: 480px) {
                    .container {
                        padding: 0 1rem;
                    }
                    
                    .quick-stats {
                        grid-template-columns: 1fr;
                        margin-top: -1.5rem;
                    }
                    
                    .card {
                        padding: 1.5rem;
                    }
                    
                    .hero-title {
                        font-size: 32px;
                    }
                    
                    .featured-image-wrapper {
                        height: 250px;
                    }
                    
                    .thumbnail-strip {
                        grid-template-columns: repeat(2, 1fr);
                        gap: 0.5rem;
                    }
                    
                    .thumbnail-image-wrapper {
                        height: 70px;
                    }
                    
                    .nav-arrow {
                        width: 35px;
                        height: 35px;
                        font-size: 14px;
                    }
                    
                    .prev-arrow {
                        left: 10px;
                    }
                    
                    .next-arrow {
                        right: 10px;
                    }
                    
                    .image-counter-badge {
                        bottom: 10px;
                        right: 10px;
                        padding: 0.5rem 1rem;
                        font-size: 12px;
                    }
                }
            `}</style>
        </div>
    );
};

export default DestinationDetail;
